// ================================================================================================
// React
import * as React from "react";

// lodash
import _ from "lodash";

// Material UI components
import { Checkbox } from "material-ui";
import FlatButton from 'material-ui/FlatButton';
import { Toolbar, ToolbarGroup, ToolbarTitle } from 'material-ui/Toolbar';

// ICONS
import ReorderIcon from 'material-ui/svg-icons/action/reorder';
import WidgetsIcon from 'material-ui/svg-icons/device/widgets';
import MobileIcon from 'material-ui/svg-icons/hardware/smartphone';
import DesktopIcon from 'material-ui/svg-icons/hardware/desktop-windows';
import ActionList from 'material-ui/svg-icons/action/list';
import ActionVisibilityOff from 'material-ui/svg-icons/action/visibility-off';
import ActionVisibility from 'material-ui/svg-icons/action/visibility';

// Internal components
import { TableCellCommonStyle } from '../Widgets/Things/Output/Table';
import { InfotipProps } from '@Primitive/Infotip';
import { isDefined, isFunction } from '@Utils/Micro';

const Sortable = React.lazy(() => import("@Primitive/Sortable"));
import { DraggableIconStyle, SortableTableRowStyle } from '@Primitive/SortableConst';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
// ================================================================================================

export interface PageViewWidgetSettingsInfo {
    Key: string;
    Title: string;
    MobileOrder: number;
    IsHidden: boolean;
    MobileIsHidden: boolean;
}

export interface PageViewSettingsProps {
    PageInfo: PageViewWidgetSettingsInfo[];
    onCloseRequest: () => void;
    onSaveRequest: (newInfo: PageViewWidgetSettingsInfo[]) => void;
    toolbarHeight: number;
}

export interface PageViewSettingsState {
    PageInfo: PageViewWidgetSettingsInfo[];
    tooltip: InfotipProps;
}

// ================================================================================================

export class PageViewSettings extends React.Component<PageViewSettingsProps, PageViewSettingsState> {
    constructor(props: PageViewSettingsProps) {
        super(props);
        this.state = {
            PageInfo: _.cloneDeep(this.props.PageInfo),
            tooltip: { open: false, content: '', xPos: 0, yPos: 0 },
        };
    }
    // --------------------------------------------------------------------------------------------
    render() {
        const sortedWidgets = _.sortBy(this.state.PageInfo, x => x.MobileOrder);
        const { toolbarHeight } = this.props;
        // todo: get somehow common style for toolbar (we can use ThemeContext)
        return <div>
            <Toolbar key="title-toolbar" style={{ boxShadow: "rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px", position: "relative", color: "white", backgroundColor: "#134459", zIndex: 1000 }} >
                <ToolbarGroup>
                    <ToolbarTitle style={{ color: "white" }} key="title-toolbar-title" text="View Settings" />
                </ToolbarGroup>
                <ToolbarGroup style={{ color: "white" }} lastChild={true}>
                    <div style={{
                        boxSizing: 'border-box',
                        textAlign: 'right',
                        display: "table",
                        marginRight: 10
                    }}>
                        {
                            _.map([
                                <FlatButton
                                    label="Cancel"
                                    style={{ margin: 0 }}
                                    secondary={true}
                                    onClick={() => {
                                        if (isFunction(this.props.onCloseRequest))
                                            this.props.onCloseRequest();
                                    }}
                                />,
                                <FlatButton
                                    label="Save"
                                    style={{ margin: 0 }}
                                    primary={true}
                                    onClick={() => {
                                        if (isFunction(this.props.onSaveRequest))
                                            this.props.onSaveRequest(this.state.PageInfo);
                                    }}
                                />
                            ],
                                (x, i) => <div key={i}
                                    style={{
                                        height: toolbarHeight,
                                        display: "table-cell",
                                        verticalAlign: "middle"
                                    }}
                                >
                                    {x}
                                </div>)
                        }
                    </div>
                </ToolbarGroup>
            </Toolbar>
            <React.Suspense fallback={<LazyLoadingAnimation />}>
                <Sortable
                    key='view-settings-table'
                    tableId='view-settings-table'
                    tableHeader={
                        <tr className='settingslist-items-tr-header' >
                            <td colSpan={1} style={TableCellCommonStyle}>
                                <ReorderIcon
                                    onMouseEnter={(ev) =>
                                        this.setState({
                                            tooltip: {
                                                open: true,
                                                content: 'The order will be applied only in mobile view',
                                                xPos: ev.pageX,
                                                yPos: ev.pageY
                                            }
                                        })}
                                    onMouseLeave={() =>
                                        this.setState({
                                            tooltip: {
                                                open: false,
                                                content: '',
                                                xPos: 0,
                                                yPos: 0
                                            }
                                        })} /></td>
                            <td colSpan={1} style={TableCellCommonStyle}>
                                <DesktopIcon />
                            </td>
                            <td colSpan={1} style={TableCellCommonStyle}>
                                <MobileIcon />
                            </td>
                            <td colSpan={2} style={TableCellCommonStyle}>
                                <WidgetsIcon />
                            </td>
                        </tr>
                    }
                    items={
                        _.map(sortedWidgets, (v, i: number) => {
                            return (
                                <tr
                                    key={'table-row' + i}
                                    className={(i % 2 == 0) ? "settingslist-items-tr-even" : "settingslist-items-tr-odd"}
                                    style={SortableTableRowStyle}>
                                    <td colSpan={1} style={TableCellCommonStyle}>
                                        <ActionList
                                            className='draggable-item'
                                            style={DraggableIconStyle} />
                                    </td>
                                    <td colSpan={1} style={TableCellCommonStyle}>
                                        <Checkbox
                                            uncheckedIcon={<ActionVisibility />}
                                            checkedIcon={<ActionVisibilityOff />}
                                            checked={v.IsHidden}
                                            onCheck={(event: React.MouseEvent<{}>, checked: boolean) =>
                                                this.setState(s => {
                                                    // Get the related widget.
                                                    let widget = _.find(s.PageInfo, x => x.Key == v.Key);

                                                    // Widget exists?
                                                    if (isDefined(widget)) {
                                                        widget.IsHidden = checked;

                                                        // For hide, update also mobile visibility.
                                                        if (checked)
                                                            widget.MobileIsHidden = checked;
                                                    }
                                                    return s;
                                                })}
                                            iconStyle={{ margin: '0 auto' }} />
                                    </td>
                                    <td colSpan={1} style={TableCellCommonStyle}>
                                        <Checkbox
                                            uncheckedIcon={<ActionVisibility />}
                                            checkedIcon={<ActionVisibilityOff />}
                                            checked={v.MobileIsHidden}
                                            onCheck={(event: React.MouseEvent<{}>, checked: boolean) =>
                                                this.setState(s => {
                                                    // Get the related widget.
                                                    let widget = _.find(s.PageInfo, x => x.Key == v.Key);

                                                    // Widget exists?
                                                    if (isDefined(widget))
                                                        widget.MobileIsHidden = checked;
                                                    return s;
                                                })}
                                            iconStyle={{ margin: '0 auto' }} />
                                    </td>
                                    <td colSpan={2} style={TableCellCommonStyle}>
                                        {v.Title}
                                    </td>
                                </tr>
                            );
                        })}
                    updateItemsOrder={(oldIndex: number, newIndex: number) => {
                        this.setState(s => {
                            _.map(sortedWidgets, (v: PageViewWidgetSettingsInfo, i: number) => {
                                // Get the related widget.
                                let widgetDesc = _.find(s.PageInfo, x => x.Key == v.Key);

                                // Update its order.
                                if (newIndex > oldIndex) {
                                    if (i < oldIndex || i > newIndex)
                                        widgetDesc.MobileOrder = i;
                                    else if (i == oldIndex)
                                        widgetDesc.MobileOrder = newIndex;
                                    else if (i > oldIndex && i <= newIndex)
                                        widgetDesc.MobileOrder = i - 1;
                                }
                                else if (oldIndex > newIndex) {
                                    if (i > oldIndex || i < newIndex)
                                        widgetDesc.MobileOrder = i;
                                    else if (i == oldIndex)
                                        widgetDesc.MobileOrder = newIndex;
                                    else if (i < oldIndex && i >= newIndex)
                                        widgetDesc.MobileOrder = i + 1;
                                }
                            });
                            return s;
                        });
                    }}
                />
            </React.Suspense>
        </div>;
    }
}
